import styled, { css } from 'styled-components';
import hexToRgba from 'hex-rgba';

export const destructiveColor = '#d52b1e';
export const specialColor = '#ff8000';
export const colorWhite = '#ffffff';
export const intuitPrimaryColor = '#0077c5';
export const turbotaxPrimaryColor = '#06b6c9';
export const mintTurbotaxIconColor = '#05a4b5';
export const secondaryGhostDarkColor = '#393a3d';
export const gray02 = '#6b6c72';
export const gray04 = '#babec5';

const destructiveStateColor = '#b80000';
const specialStateColor = '#f95700';
const quickbooksPrimaryColor = '#2ca01c';
const quickbooksMiniInversePrimaryColor = '#393a3d';
const mintPrimaryColor = '#06b6c9';
const miniInversePrimaryOverlayColor = '#000000';
const secondaryColor = '#8d9096';
const secondaryGhostColor = '#f4f5f8';
const intuitStateColor = '#055393';
const quickbooksMiniInverseFocusColor = '#34bfff';

const bgColors = {
  primary: intuitPrimaryColor,
  secondary: '#d4d7dc',
  secondaryGhost: 'transparent',
  tertiary: 'transparent',
  icon: 'transparent',
  destructive: destructiveColor,
  special: specialColor,
  destructiveTertiary: 'transparent'
};

const buttonSizes = {
  mini: {
    minHeight: '24px',
    minWidth: '80px'
  },
  standard: {
    minHeight: '36px',
    minWidth: '100px'
  },
  responsive: {
    minHeight: '40px',
    minWidth: '100px'
  },
  jumbo: {
    minHeight: '48px',
    minWidth: '120px'
  }
};

const data = {
  intuit: {
    themeBgColors: {
      primary: intuitPrimaryColor,
      secondary: bgColors.secondary,
      tertiary: 'transparent',
      secondaryGhost: 'transparent',
      icon: 'transparent',
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructiveTertiary
    },
    darkThemeBgColors: {
      primary: intuitPrimaryColor,
      secondary: bgColors.secondary,
      tertiary: 'transparent',
      secondaryGhost: 'transparent',
      icon: 'transparent',
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructiveTertiaryTertiary
    },
    themeBgStateColors: {
      primary: intuitStateColor,
      secondary: secondaryColor,
      secondaryGhost: intuitPrimaryColor,
      tertiary: intuitPrimaryColor,
      icon: intuitPrimaryColor,
      destructive: destructiveStateColor,
      special: specialStateColor,
      destructiveTertiary: destructiveColor
    },
    darkThemeBgStateColors: {
      primary: intuitStateColor,
      secondary: secondaryColor,
      secondaryGhost: secondaryGhostColor,
      tertiary: secondaryGhostColor,
      icon: secondaryGhostColor,
      destructive: destructiveStateColor,
      special: specialStateColor,
      destructiveTertiary: destructiveColor
    },
    iconFillColors: {
      primary: colorWhite,
      secondary: secondaryGhostDarkColor,
      secondaryGhost: intuitPrimaryColor,
      tertiary: intuitPrimaryColor,
      icon: intuitPrimaryColor,
      destructive: colorWhite,
      special: colorWhite,
      destructiveTertiary: destructiveColor
    },
    darkThemeIconFillColors: {
      primary: colorWhite,
      secondary: secondaryGhostDarkColor,
      secondaryGhost: colorWhite,
      tertiary: colorWhite,
      icon: colorWhite,
      destructive: colorWhite,
      special: colorWhite,
      destructiveTertiary: destructiveColor
    },
    focusColorLight: {
      primary: intuitPrimaryColor,
      secondary: intuitPrimaryColor,
      tertiary: intuitPrimaryColor,
      secondaryGhost: intuitPrimaryColor,
      icon: intuitPrimaryColor,
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructive
    },
    focusColorDark: {
      primary: colorWhite,
      secondary: colorWhite,
      tertiary: colorWhite,
      secondaryGhost: colorWhite,
      icon: colorWhite,
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructive
    },
    buttonSizes,
    disabledOpacity: 0.4
  },
  quickbooks: {
    themeBgColors: {
      primary: quickbooksPrimaryColor,
      secondary: 'transparent',
      tertiary: 'transparent',
      secondaryGhost: 'transparent',
      icon: 'transparent',
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructiveTertiary
    },
    darkThemeBgColors: {
      primary: quickbooksPrimaryColor,
      secondary: 'transparent',
      tertiary: 'transparent',
      secondaryGhost: 'transparent',
      icon: 'transparent',
      primarymini: quickbooksMiniInversePrimaryColor,
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructiveTertiary
    },
    themeBgStateColors: {
      primary: '#108000',
      secondary: secondaryColor,
      secondaryGhost: secondaryGhostDarkColor,
      tertiary: gray02,
      icon: gray02,
      destructive: destructiveStateColor,
      special: specialStateColor,
      destructiveTertiary: destructiveColor
    },
    darkThemeBgStateColors: {
      primary: secondaryGhostDarkColor,
      primarymini: miniInversePrimaryOverlayColor,
      secondary: secondaryColor,
      secondaryGhost: secondaryGhostColor,
      tertiary: secondaryGhostColor,
      icon: secondaryGhostColor,
      destructive: destructiveStateColor,
      special: specialStateColor,
      destructiveTertiary: destructiveColor
    },
    iconFillColors: {
      primary: colorWhite,
      secondary: secondaryGhostDarkColor,
      secondaryGhost: secondaryGhostDarkColor,
      tertiary: intuitPrimaryColor,
      icon: gray02,
      iconActive: secondaryGhostDarkColor,
      destructive: colorWhite,
      special: colorWhite,
      destructiveTertiary: destructiveColor
    },
    darkThemeIconFillColors: {
      primary: colorWhite,
      secondary: colorWhite,
      secondaryGhost: colorWhite,
      tertiary: colorWhite,
      icon: colorWhite,
      destructive: colorWhite,
      special: colorWhite,
      destructiveTertiary: destructiveColor
    },
    buttonSizes: {
      ...buttonSizes,
      mini: { ...buttonSizes.mini, borderRadius: '12px' },
      standard: { ...buttonSizes.standard, borderRadius: '18px' },
      jumbo: { ...buttonSizes.jumbo, borderRadius: '24px' },
      responsive: { minHeight: '36px', minWidth: '100px', borderRadius: '20px' }
    },
    focusColorLight: {
      primary: intuitPrimaryColor,
      secondary: intuitPrimaryColor,
      tertiary: intuitPrimaryColor,
      secondaryGhost: intuitPrimaryColor,
      icon: intuitPrimaryColor,
      primarymini: intuitPrimaryColor,
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructive
    },
    focusColorDark: {
      primary: quickbooksMiniInverseFocusColor,
      secondary: quickbooksMiniInverseFocusColor,
      tertiary: quickbooksMiniInverseFocusColor,
      secondaryGhost: quickbooksMiniInverseFocusColor,
      icon: quickbooksMiniInverseFocusColor,
      primarymini: intuitPrimaryColor,
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructive
    },
    focusBoxShadow: '0 0 0 2px',
    focusBorderRadius: '20px',
    focusOpacity: 1,
    iconBorderRadius: '8px',
    disabledOpacity: 0.4
  },
  turbotax: {
    themeBgColors: {
      primary: turbotaxPrimaryColor,
      secondary: bgColors.secondary,
      tertiary: 'transparent',
      secondaryGhost: 'transparent',
      icon: 'transparent',
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructiveTertiary
    },
    darkThemeBgColors: {
      primary: colorWhite,
      secondary: bgColors.secondary,
      tertiary: 'transparent',
      secondaryGhost: 'transparent',
      icon: 'transparent',
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructiveTertiary
    },
    themeBgStateColors: {
      primary: '#037c8f',
      secondary: secondaryColor,
      secondaryGhost: '#0493a3',
      tertiary: turbotaxPrimaryColor,
      icon: turbotaxPrimaryColor,
      destructive: destructiveStateColor,
      special: specialStateColor,
      destructiveTertiary: destructiveColor
    },
    darkThemeBgStateColors: {
      primary: turbotaxPrimaryColor,
      secondary: secondaryColor,
      secondaryGhost: secondaryGhostColor,
      tertiary: secondaryColor,
      icon: secondaryGhostColor,
      destructive: destructiveStateColor,
      special: specialStateColor,
      destructiveTertiary: destructiveColor
    },
    iconFillColors: {
      primary: colorWhite,
      secondary: secondaryGhostDarkColor,
      secondaryGhost: mintTurbotaxIconColor,
      tertiary: turbotaxPrimaryColor,
      icon: mintTurbotaxIconColor,
      destructive: colorWhite,
      special: colorWhite,
      destructiveTertiary: destructiveColor
    },
    darkThemeIconFillColors: {
      primary: turbotaxPrimaryColor,
      secondary: secondaryGhostDarkColor,
      secondaryGhost: colorWhite,
      tertiary: colorWhite,
      icon: colorWhite,
      destructive: colorWhite,
      special: colorWhite,
      destructiveTertiary: destructiveColor
    },
    buttonSizes,
    focusColorLight: {
      primary: turbotaxPrimaryColor,
      secondary: intuitPrimaryColor,
      tertiary: turbotaxPrimaryColor,
      secondaryGhost: intuitPrimaryColor,
      icon: turbotaxPrimaryColor,
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructive
    },
    focusColorDark: {
      primary: colorWhite,
      secondary: colorWhite,
      tertiary: colorWhite,
      secondaryGhost: colorWhite,
      icon: colorWhite,
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructive
    },
    disabledOpacity: 0.4
  },
  mint: {
    themeBgColors: {
      primary: mintPrimaryColor,
      secondary: bgColors.secondary,
      secondaryGhost: 'transparent',
      tertiary: 'transparent',
      icon: mintPrimaryColor,
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructiveTertiary
    },
    darkThemeBgColors: {
      primary: colorWhite,
      secondary: bgColors.secondary,
      secondaryGhost: 'transparent',
      tertiary: 'transparent',
      icon: 'transparent',
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructiveTertiary
    },
    themeBgStateColors: {
      primary: mintTurbotaxIconColor,
      secondary: secondaryColor,
      secondaryGhost: mintTurbotaxIconColor,
      tertiary: mintPrimaryColor,
      icon: mintPrimaryColor,
      destructive: destructiveStateColor,
      special: specialStateColor,
      destructiveTertiary: destructiveColor
    },
    darkThemeBgStateColors: {
      primary: mintPrimaryColor,
      secondary: secondaryColor,
      secondaryGhost: secondaryGhostColor,
      tertiary: secondaryColor,
      icon: secondaryGhostColor,
      destructive: destructiveStateColor,
      special: specialStateColor,
      destructiveTertiary: destructiveColor
    },
    iconFillColors: {
      primary: colorWhite,
      secondary: secondaryGhostDarkColor,
      secondaryGhost: mintTurbotaxIconColor,
      tertiary: mintPrimaryColor,
      icon: mintTurbotaxIconColor,
      destructive: colorWhite,
      special: colorWhite,
      destructiveTertiary: destructiveColor
    },
    darkThemeIconFillColors: {
      primary: mintPrimaryColor,
      secondary: secondaryGhostDarkColor,
      secondaryGhost: colorWhite,
      tertiary: colorWhite,
      icon: colorWhite,
      destructive: colorWhite,
      special: colorWhite,
      destructiveTertiary: destructiveColor
    },
    buttonSizes,
    focusColorLight: {
      primary: mintPrimaryColor,
      secondary: intuitPrimaryColor,
      tertiary: mintPrimaryColor,
      secondaryGhost: intuitPrimaryColor,
      icon: mintPrimaryColor,
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructive
    },
    focusColorDark: {
      primary: colorWhite,
      secondary: colorWhite,
      tertiary: colorWhite,
      secondaryGhost: colorWhite,
      icon: colorWhite,
      destructive: bgColors.destructive,
      special: bgColors.special,
      destructiveTertiary: bgColors.destructive
    },
    disabledOpacity: 0.4
  }
};

const hoverOpacity = {
  primary: 50,
  secondary: 50,
  secondaryGhost: 25,
  tertiary: 25,
  icon: 25,
  destructive: 50,
  destructiveTertiary: 25,
  special: 50
};

const activeOpacity = {
  primary: 100,
  secondary: 100,
  secondaryGhost: 40,
  tertiary: 40,
  icon: 40,
  destructive: 100,
  destructiveTertiary: 40,
  special: 100
};

const activeDarkOpacity = {
  primary: 60,
  secondary: 100,
  secondaryGhost: 40,
  tertiary: 40,
  icon: 40,
  destructive: 100,
  destructiveTertiary: 40,
  special: 100
};

const disabledStyle = css`
  cursor: default;
  pointer-events: none;
  opacity: ${props => data[props.theme].disabledOpacity};
  fill: ${props =>
    props.theme === 'quickbooks' &&
    (props.inverse ? gray02 : gray04)} !important;
`;

const iconStyle = css`
  height: 100%;
  height: 36px;
  width: 36px;
  border-radius: ${props => data[props.theme].iconBorderRadius || '4px'};
`;

const secondaryGhostStyle = css`
  ${props => `
    border: 2px solid ${data[props.theme].themeBgColors.primary};
  `}
  ${props =>
    props.inverse &&
    `
    border-color: ${colorWhite};
  `}
  &:before {
    width: calc(100% + 6px);
    height: calc(100% + 6px);
    left: -3px;
    top: -3px;
  }
`;

/**
 * Returns the button focus radius
 *
 * @param Object - {
 *  theme - theme input prop
 *  buttonType - buttonType input prop
 * }
 * @returns radius
 */
const getFocusBorderRadius = ({ theme, buttonType }) => {
  let radius = data[theme].focusBorderRadius || '5px';
  if (data[theme].focusBorderRadius && buttonType === 'icon') {
    radius = '8px';
  }

  return radius;
};

/**
 * Returns the button overlay radius
 *
 * @param Object - {
 *  theme - theme input prop
 *  size - size input prop
 *  buttonType - buttonType input prop
 * }
 * @returns radius
 */
const getOverlayBorderRadius = ({ theme, size, buttonType }) => {
  // Quickbooks border-radius
  const { borderRadius } =
    data[theme].buttonSizes && data[theme].buttonSizes[size];
  const radius =
    buttonType === 'secondaryGhost' ? '2px' : borderRadius || '4px';
  return radius;
};

const Overlay = styled.span`
  display: block;
  width: 100%;
  border-radius: ${props => getOverlayBorderRadius(props)};
  ${props => props.buttonType === 'icon' && iconStyle};
`;

/**
 * Returns the button background color
 *
 * @param Object -{
 *  theme - theme input prop
 *  inverse - inverse input prop
 *  buttonType - buttonType input prop
 *  size - size input prop
 *  loading - loading input prop
 * }
 * @returns button background color
 */
const getButtonBgColor = ({ theme, inverse, buttonType, size, loading }) => {
  const { darkThemeBgColors, themeBgColors } = data[theme];
  let color = themeBgColors[buttonType];
  if (inverse) {
    color =
      darkThemeBgColors[`${buttonType}${size}`] ||
      darkThemeBgColors[buttonType];
  }

  if (loading && themeBgColors[buttonType] !== 'transparent') {
    color = hexToRgba(themeBgColors[buttonType], 40);
  }

  return color;
};

/**
 * Returns the button focus color
 *
 * @param Object - {
 *  theme - theme input prop
 *  inverse - inverse input prop
 *  buttonType - buttonType input prop
 *  size - size input prop
 * }
 * @returns button focus color
 */
const getButtonFocusColor = ({ theme, inverse, buttonType, size }) => {
  let color;

  const { focusColorLight, focusColorDark } = data[theme];

  if (inverse) {
    color =
      focusColorDark[`${buttonType}${size}`] || focusColorDark[buttonType];
  } else {
    color = focusColorLight[buttonType];
  }

  return color;
};

/**
 * Returns the button border radius
 *
 * @param Object - {
 *  theme - theme input prop
 *  size - size input prop
 * }
 * @returns button border radius
 */
const getBorderRadius = ({ theme, size }) => {
  // borderRadius is only for quickbooks
  const { borderRadius } =
    data[theme].buttonSizes && data[theme].buttonSizes[size];
  const radius = borderRadius || '4px';
  return radius;
};

/**
 * Returns the button overlay color
 *
 * @param Object - {
 *  theme - theme input prop
 *  inverse - inverse input prop
 *  loading - loading input prop
 *  buttonType - buttonType input prop
 *  size - size input prop
 * }
 * @param isHover - true if color for hover
 * @returns button overlay color
 */
// still not ideal, we need to refactor to light/dark as top level
const getButtonOverlayColor = (
  { theme, inverse, loading, buttonType, size },
  isHover
) => {
  let color;

  const { themeBgStateColors, darkThemeBgStateColors } = data[theme];

  // eslint-disable-next-line no-nested-ternary
  const opacity = isHover
    ? hoverOpacity[buttonType]
    : inverse
    ? activeDarkOpacity[buttonType]
    : activeOpacity[buttonType];

  if (inverse) {
    // `primarymini` check
    color = hexToRgba(
      darkThemeBgStateColors[`${buttonType}${size}`] ||
        darkThemeBgStateColors[buttonType],
      opacity
    );
  } else {
    color = hexToRgba(themeBgStateColors[buttonType], opacity);
  }

  color = loading ? 'transparent!important' : color;
  return color;
};

const buttonOverlayStyle = css`
  ${props =>
    data[props.theme] &&
    `
    &:hover ${Overlay} {
      background-color: ${getButtonOverlayColor(props, true)};
    }
    &:active ${Overlay} {
      background-color: ${getButtonOverlayColor(props)};
    }
  `}
`;

const buttonSizeStyle = css`
  ${props => `
    min-height: ${data[props.theme].buttonSizes[props.size].minHeight};
    min-width: ${data[props.theme].buttonSizes[props.size].minWidth};
  `}
`;

const Wrapper = styled.button`
    font-family: inherit;
    box-sizing: border-box;
    position: relative;
    outline: none;
    padding: 0;
    margin: 0;
    border: none;
    background-color: ${props => getButtonBgColor(props)};
    border-radius: ${props => getBorderRadius(props)};
    &[data-a11yfocus] {
      &:focus:not(:active):before {
        box-shadow: ${props => data[props.theme].focusBoxShadow || '0 0 0 2px'};
        color: ${props => getButtonFocusColor(props)};
        opacity: ${props => data[props.theme].focusOpacity || 0.5};
      }
    }
    cursor: ${props => (props.loading ? 'none' : 'pointer')};
    fill: ${props =>
      props.inverse
        ? data[props.theme].darkThemeIconFillColors[props.buttonType]
        : data[props.theme].iconFillColors[props.buttonType]};
    ${props => props.buttonType !== 'icon' && buttonSizeStyle};
    ${props => !props.disabled && buttonOverlayStyle}
    &:active {
      fill: ${props =>
        data[props.theme] &&
        data[props.theme].iconFillColors[`${props.buttonType}Active`]}
    }
    &:before {
      opacity: 0;
      content: "";
      border-radius: ${props => getFocusBorderRadius(props)};
      display: block;
      position: absolute;
      width: calc(100% + 2px);
      height: calc(100% + 2px);
      left: -1px;
      top: -1px;
    }
    ${props => props.buttonType === 'icon' && iconStyle};
    ${props =>
      props.theme === 'quickbooks' &&
      props.buttonType === 'secondary' &&
      `
      border: ${
        props.inverse
          ? `2px solid ${colorWhite}`
          : `2px solid ${secondaryGhostDarkColor}`
      };
      &:before {
        width: calc(100% + 6px);
        height: calc(100% + 6px);
        left: -3px;
        top: -3px;
      }
    `}
    ${props =>
      props.theme === 'quickbooks' &&
      props.buttonType === 'secondary' &&
      props.size === 'mini' &&
      `
      border-width: 1px;
    `}
    ${props => props.buttonType === 'secondaryGhost' && secondaryGhostStyle}
    ${props => props.disabled && disabledStyle}
    ::-moz-focus-inner {
      border: 0;
    }
  `;
export { Overlay, Wrapper };
